import { ChatBlackIcon, RefundIcon, StarIcon } from '@/components/assets/icons/svgs';
import ContentContainer from '@/components/containers/contentContainer';
import CustomButton from '@/shared/button/customButton';
import { api } from '@/utils/api';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Popup from '@/shared/popups/popupDetails';
import { Divider, IconButton, Pagination, Rating, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import TextArea from '@/shared/textArea/textArea';
import { StripeCheckout } from '@/shared/stripe/StripeCheckout';
import { JobStatusButtonText_User, JobStatusDetailStatus } from '@/shared/utils/enum';
import { toast } from "@/utils/appToast";
import LiveLocation from '@/components/dashboard-pages/common-dashboard/location/LiveLocation';
import VerifySubmission from '@/shared/popups/verify';
import { Spinner } from '@/shared/spinner/spinner';
import { useNavigate } from 'react-router-dom';
import { FilterI } from '@/types/interfaces';
import { useTranslation } from 'react-i18next';
import JobSkeleton from '@/utils/jobskeleton'
import { trackUserAction } from '@/utils/analytics';
import {
  trackMetaInitiateCheckout,
  trackMetaPurchase,
} from '@/utils/metaPixel';
import Resizer from "react-image-file-resizer";
import JobPaymentSummarySection from '@/components/job/JobPaymentSummarySection';
import JobCommissionBreakdown from '@/components/job/JobCommissionBreakdown';
import {
  extractPaymentSummaryFromResponse,
  getPaymentSummary,
} from '@/utils/jobPaymentSummary';
import type { JobPaymentSummary } from '@/types/jobPaymentTypes';
import { getJobCommissions } from '@/utils/jobCommissions';
import { getBackendCallErrorMessage } from '@/utils/backendErrorMessage';
import AssignedVoucherPicker from '@/components/vouchers/AssignedVoucherPicker';
import { filterUsableVouchersForAudience } from '@/utils/vouchers';
import type { MyVoucher } from '@/types/jobPaymentTypes';

const MAX_FEEDBACK_LENGTH = 2000;

/** Round API money values to cents (matches backend totalCharge validation). */
const roundMoney = (value: number | string | null | undefined): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

/** Normalize payment estimate from backendCall wrapper (body or body.data). */
const unwrapPaymentEstimate = (res: any) => {
  if (!res?.success) return null;
  const body = res.data;
  if (!body || typeof body !== 'object') return null;
  if (body.totalCharge != null || body.budget != null || body.clientCharge != null) {
    return body;
  }
  if (body.data && typeof body.data === 'object') {
    return body.data;
  }
  return body;
};

const mergeJobPaymentSummary = (job: any, summary?: JobPaymentSummary) => {
  if (!summary) return job;
  return { ...job, paymentSummary: summary };
};

const MyProposals = () => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<any>({});
  const { user } = useSelector((state: { user }) => state.user);
  const [selectedProposal, setSelectedProposal] = useState<any>(null);
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();
  // Rating popup state
  const [showRatingPopup, setShowRatingPopup] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
// state for image preview popup
const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [showStripePopup, setShowStripePopup] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [showPaymentDetailsPopup, setShowPaymentDetailsPopup] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [savedPaymentMethodId, setSavedPaymentMethodId] = useState<string | null>(null);
  const [paymentFlowStep, setPaymentFlowStep] = useState<'initial' | 'stripe_selected' | 'ath_selected'>('initial');
  const [showPaymentMethodChoice, setShowPaymentMethodChoice] = useState(false);
  const [showAthPhonePopup, setShowAthPhonePopup] = useState(false);
  const [athPhoneNumber, setAthPhoneNumber] = useState('');
  const [isValidatingAthPhone, setIsValidatingAthPhone] = useState(false);
  const [voucherCodeInput, setVoucherCodeInput] = useState('');
  const [appliedVoucherCode, setAppliedVoucherCode] = useState<string | null>(null);
  const [isApplyingVoucher, setIsApplyingVoucher] = useState(false);
  const [usableAssignedVouchers, setUsableAssignedVouchers] = useState<MyVoucher[]>([]);

  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ jobId: string; action: string; label: string , budget?: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Dispute form state
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [showPostServiceModal, setShowPostServiceModal] = useState(false);
  const [postServiceJob, setPostServiceJob] = useState<any>(null);
  const [disputeIssueType, setDisputeIssueType] = useState<string>('');
  const [disputeDescription, setDisputeDescription] = useState<string>('');
  const [disputeImages, setDisputeImages] = useState<File[]>([]);
  const [disputeImagePreviews, setDisputeImagePreviews] = useState<string[]>([]);
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);
  const [activeDisputes, setActiveDisputes] = useState<Record<string, any>>({});
  const [fetchingJobs, setFetchingJobs] = useState(false);
  const [isPaymentInitiated, setIsPaymentInitiated] = useState(false);
  const [offset, setOffset] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const hasPageBeenRendered = useRef(false);
  const [filterValue, setFilterValue] = useState<FilterI>({
    text: '',
    page: offset,
    limit: pageSize,
    order: 'desc',
    clientName: '',
    workerName: '',
    status: ''
  });

  const loadUsableClientVouchers = async () => {
    try {
      const mineRes = await api.getMyVouchers();
      if (mineRes?.success) {
        setUsableAssignedVouchers(
          filterUsableVouchersForAudience(mineRes.data || [], 'client')
        );
      } else {
        setUsableAssignedVouchers([]);
      }
    } catch {
      setUsableAssignedVouchers([]);
    }
  };

  const openPaymentDetailsForJob = async (jobId: string) => {
    try {
      // Reset voucher state when switching jobs.
      setVoucherCodeInput('');
      setAppliedVoucherCode(null);
      setUsableAssignedVouchers([]);

      const [jobPaymentDetails] = await Promise.all([
        api.getJobPaymentDetails(jobId),
        loadUsableClientVouchers(),
      ]);
      const estimate = unwrapPaymentEstimate(jobPaymentDetails);
      if (estimate) {
        setPaymentDetails(estimate);
        setPaymentFlowStep('initial');
        setShowPaymentDetailsPopup(true);
      } else {
        toast.error(jobPaymentDetails?.message || 'No se pudieron cargar los detalles del pago');
      }
    } catch {
      toast.error('No se pudieron cargar los detalles del pago');
    }
  };

  const handleApplyVoucher = async (codeOverride?: string) => {
    if (!selectedProposal?._id) return;
    const trimmed = (typeof codeOverride === 'string' ? codeOverride : voucherCodeInput).trim();
    if (!trimmed) return;

    setIsApplyingVoucher(true);
    try {
      const res = await api.getJobPaymentDetails(selectedProposal._id, trimmed);
      const estimate = unwrapPaymentEstimate(res);
      if (estimate) {
        setPaymentDetails(estimate);
        const normalized = (estimate?.voucherCode || trimmed).toString().toUpperCase();
        setAppliedVoucherCode(normalized);
        setVoucherCodeInput(normalized);
      } else {
        toast.error(getBackendCallErrorMessage(res) || t('voucher_invalid'));
      }
    } catch (err) {
      toast.error(t('voucher_invalid'));
    } finally {
      setIsApplyingVoucher(false);
    }
  };

  const handleClearVoucher = async () => {
    if (!selectedProposal?._id) return;

    setIsApplyingVoucher(true);
    try {
      const res = await api.getJobPaymentDetails(selectedProposal._id);
      const estimate = unwrapPaymentEstimate(res);
      if (estimate) {
        setPaymentDetails(estimate);
        setAppliedVoucherCode(null);
        setVoucherCodeInput('');
      } else {
        toast.error(getBackendCallErrorMessage(res) || t('voucher_invalid'));
      }
    } catch (err) {
      toast.error(t('voucher_invalid'));
    } finally {
      setIsApplyingVoucher(false);
    }
  };

  const handleStripePay = async () => {
    setStripeLoading(true);
    const res = await api.createSetupIntent({ stripeId: user.stripeId });
    const secret = res?.data?.client_secret || res?.client_secret;
    if (secret) {
      setStripeClientSecret(secret);
      setShowStripePopup(true);
    }
    setStripeLoading(false);
  };



  const fetchJobs = async () => {

    try {
      const res = await api.getMyJobs({ userId: user._id, type: "hire", page: offset, limit: pageSize });
      if (res && res.success) {
        setJobs(res.data || {});
        setFetchingJobs(false);
        hasPageBeenRendered.current = true;
      }
    } catch (err) {
      setFetchingJobs(false);
    }
  };

  useEffect(() => {
    setFetchingJobs(true);
    fetchJobs();
    fetchActiveDisputes();
  }, []);

  // Helper to get service name from job object
  const getServiceName = (job: any) => {
    if (typeof job.service === 'object' && job.service?.name) return job.service.name;
    if (typeof job.service === 'string') return job.service;
    return '-';
  };

  // Submit review and rating
  const handleSubmitReview = async () => {
    if (!selectedProposal || !rating) {
      toast.error('Por favor proporciona una calificación.');
      return;
    }
    try {
      const res = await api.rateWorker({ jobId: selectedProposal._id, workerId: selectedProposal.assignedWorker?._id, rating, review: feedback });
      if (res && res.success) {
        toast.success('Reseña enviada correctamente');
        setShowRatingPopup(false);
        setFeedback('');
        setRating(0);
        fetchJobs();
      } else {
        toast.error(res?.message || 'No se pudo enviar la reseña');
      }
    } catch (err) {
      toast.error('No se pudo enviar la reseña');
    }
  };

  // Star rating component
  const addStripePaymentMethod = async (paymentMethodId: string) => {
    const res = await api.attachJobPaymentMethod(selectedProposal?._id, { paymentMethodId });
    if (res && res.success) {
      const [jobPaymentDetails] = await Promise.all([
        appliedVoucherCode
          ? api.getJobPaymentDetails(selectedProposal._id, appliedVoucherCode)
          : api.getJobPaymentDetails(selectedProposal._id),
        loadUsableClientVouchers(),
      ]);
      const estimate = unwrapPaymentEstimate(jobPaymentDetails);
      if (estimate) {
        setPaymentDetails(estimate);
        setPaymentFlowStep('stripe_selected');
        setShowPaymentDetailsPopup(true);
        setShowStripePopup(false);
      }
      toast.success('Método de pago agregado correctamente');
    } else {
      toast.error('No se pudo agregar el método de pago');
    }
  };

  const initiateStripeJobPayment = async (jobId: string) => {
    if (!savedPaymentMethodId || !paymentDetails?.totalCharge) return;
    const voucherMismatch =
      !!appliedVoucherCode &&
      voucherCodeInput.trim().toUpperCase() !== appliedVoucherCode;
    if (isApplyingVoucher || voucherMismatch) {
      toast.error(t('voucher_apply_required'));
      return;
    }
    setIsPaymentInitiated(true);
    const res = await api.initiateJobPayment(jobId, {
      totalCharge: roundMoney(paymentDetails.totalCharge),
      clientPaymentMethod: 'STRIPE',
      ...(appliedVoucherCode ? { voucherCode: appliedVoucherCode } : {}),
    });
    if (res?.success) {
      const summary = extractPaymentSummaryFromResponse(res);
      if (summary?.clientPaymentStatus === 'confirmed') {
        toast.success(res.message || 'Pago iniciado correctamente');
      } else {
        toast.success(res.message || 'Pago iniciado correctamente');
      }
      trackMetaPurchase({
        value: roundMoney(paymentDetails.totalCharge),
        currency: 'USD',
        content_ids: jobId,
        content_type: 'product',
      });
      if (summary && selectedProposal?._id === jobId) {
        setSelectedProposal(mergeJobPaymentSummary(selectedProposal, summary));
      }
      setShowPaymentDetailsPopup(false);
      setSavedPaymentMethodId(null);
      setPaymentFlowStep('initial');
      setVoucherCodeInput('');
      setAppliedVoucherCode(null);
      fetchJobs();
    } else {
      toast.error(getBackendCallErrorMessage(res) || 'No se pudo iniciar el pago');
    }
    setIsPaymentInitiated(false);
  };

  const initiateAthJobPayment = async (jobId: string, phoneNumber: string) => {
    if (!paymentDetails?.totalCharge) return;
    const voucherMismatch =
      !!appliedVoucherCode &&
      voucherCodeInput.trim().toUpperCase() !== appliedVoucherCode;
    if (isApplyingVoucher || voucherMismatch) {
      toast.error(t('voucher_apply_required'));
      return;
    }
    setIsPaymentInitiated(true);
    try {
      const res = await api.initiateJobPayment(jobId, {
        totalCharge: roundMoney(paymentDetails.totalCharge),
        clientPaymentMethod: 'ATH',
        phoneNumber: phoneNumber.replace(/\D/g, ''),
        ...(appliedVoucherCode ? { voucherCode: appliedVoucherCode } : {}),
      });
      if (res?.success) {
        const summary = extractPaymentSummaryFromResponse(res);
        const isPending =
          summary?.clientPaymentStatus === 'pending' || res.status === 'pending';
        toast.success(
          isPending ? t('athPaymentPendingMessage') : (res.message || 'Solicitud de pago ATH enviada correctamente')
        );
        // Only count as Purchase when ATH payment is confirmed (not pending request).
        if (!isPending) {
          trackMetaPurchase({
            value: roundMoney(paymentDetails.totalCharge),
            currency: 'USD',
            content_ids: jobId,
            content_type: 'product',
          });
        }
        if (summary && selectedProposal?._id === jobId) {
          setSelectedProposal(mergeJobPaymentSummary(selectedProposal, summary));
        }
        setShowPaymentDetailsPopup(false);
        setShowAthPhonePopup(false);
        setAthPhoneNumber('');
        setPaymentFlowStep('initial');
        setVoucherCodeInput('');
        setAppliedVoucherCode(null);
        fetchJobs();
      } else {
        toast.error(getBackendCallErrorMessage(res) || 'No se pudo enviar la solicitud de pago ATH');
      }
    } catch {
      toast.error('No se pudo enviar la solicitud de pago ATH');
    } finally {
      setIsPaymentInitiated(false);
    }
  };

  const handleStartJob = async (jobId: string) => {
    const res = await api.startJob(jobId);
    if (res && res.success) {
      toast.success('Trabajo iniciado correctamente');
      fetchJobs();
    }
    else {
      toast.error('No se pudo iniciar el trabajo');
    }
  }

  const handleFinishJob = async (jobId: string) => {
    const res = await api.finishJob(jobId);
    if (res && res.success) {
      toast.success('Trabajo finalizado correctamente');
      fetchJobs();
    }
    else {
      toast.error('No se pudo finalizar el trabajo');
    }
  }

  const handleRefundPayment = async (jobId: string) => {
    const res = await api.refundPayment(jobId, user._id);
    if (res && res.success) {
      toast.success('Pago reembolsado correctamente');
      fetchJobs();
    }
    else {
      toast.error('No se pudo reembolsar el pago');
    }
  }
  // 
  const handleMarkAsReached = async (jobId: string) => {
    const res = await api.clientMarkedReched(jobId);
    if (res && res.success) {
      toast.success('Trabajo marcado como llegado correctamente');
      fetchJobs();
    }
    else {
      console.log("res", res)
      if (res && res.message == "Job application not found or already marked as reached") {
        toast.error('El trabajo ya fue marcado como llegado');
        fetchJobs();
      }
      else {
        toast.error('No se pudo marcar como llegado');
      }
    }
  }

  const handleReleasePayment = async (jobId: string) => {
    const res = await api.releasePayment(jobId);
    if (res && res.success) {
      const summary = extractPaymentSummaryFromResponse(res);
      const message =
        res.message ||
        res.data?.message ||
        (summary ? t('payoutDelayedMessage') : 'Pago liberado correctamente');
      toast.success(message);

      if (summary && selectedProposal?._id === jobId) {
        setSelectedProposal(mergeJobPaymentSummary(selectedProposal, summary));
      }
      
      trackUserAction('payment_released', {
        job_id: jobId,
        user_type: 'client',
        action: 'release_payment'
      });
      
      fetchJobs();
    }
    else {
      toast.error(res?.message || 'No se pudo liberar los fondos');
    }
  }

  // buttons: [
  //       { label: t('accept'), className: 'bg-green-500 text-white', labelStyle: 'text-white', action: 'accept' },
  //       { label: t('reject'), className: 'bg-red-500 text-white', labelStyle: 'text-white', action: 'reject' }
  //     ]

// Accept proposal function — after confirm, show Stripe/ATH choice (not Stripe directly)
const acceptProposal = async (jobID: string) => {
  try {
    setIsLoading(true);
    const res = await api.clientAcceptJob(jobID, { status: "accepted", location });
    if (res && res.success) {
      trackUserAction('job_accepted', { job_id: jobID, user_type: 'client', status: 'accepted' });
      trackMetaInitiateCheckout({
        content_ids: jobID,
        content_type: 'product',
        value: roundMoney(
          selectedProposal?._id === jobID
            ? (selectedProposal?.budget ?? paymentDetails?.totalCharge ?? 0)
            : 0
        ),
        currency: 'USD',
      });
      fetchJobs();
      setPaymentFlowStep('initial');
      setShowPaymentMethodChoice(true);
    } else {
      toast.error(res?.message || 'No se pudo aceptar la propuesta');
    }
  } catch (error) {
    toast.error(error?.message || 'No se pudo aceptar la propuesta');
  } finally {
    setIsLoading(false);
  }
};

// Reject proposal function
const rejectProposal = async (jobID: string) => {
  try {
    setIsLoading(true);
    const res = await api.clientAcceptJob(jobID, { status: "rejected", location });
    if (res && res.success) {
      toast.success('Propuesta rechazada correctamente');
      fetchJobs();
    } else {
      toast.error(res?.message || 'No se pudo rechazar la propuesta');
    }
  } catch (error) {
    toast.error(error?.message  || 'No se pudo rechazar la propuesta');
  } finally {
    setIsLoading(false);
  }
};


  const confirmStatusChange = async () => {
   
    if (pendingAction) {
      setIsLoading(true);
      try {
         if (pendingAction.action === 'accept') await acceptProposal(pendingAction.jobId);
    if (pendingAction.action === 'reject') await rejectProposal(pendingAction.jobId);
        if (pendingAction.action === 'start_job') await handleStartJob(pendingAction.jobId);
        else if (pendingAction.action === 'worker_on_way') await handleMarkAsReached(pendingAction.jobId);
        else if (pendingAction.action === 'finish_job') await handleFinishJob(pendingAction.jobId);
        else if (pendingAction.action === 'release_payment') await handleReleasePayment(pendingAction.jobId);
        else if (pendingAction.action === 'refund_payment') await handleRefundPayment(pendingAction.jobId);
      } finally {
        setIsLoading(false);
        setShowVerificationPopup(false);
        setPendingAction(null);
      }
    }
  };
  const cancelStatusChange = () => {
    setShowVerificationPopup(false);
    setPendingAction(null);
  };

  useEffect(() => {
    if (hasPageBeenRendered.current == true) {
      const delayDebounceFn = setTimeout(() => {
        fetchJobs();
      }, 300);


      return () => clearTimeout(delayDebounceFn);
    }
  }, [filterValue]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setOffset(newPage);
    setFilterValue({ ...filterValue, page: newPage })
  }
  // 

const isRefundable = (job: any) => {
  return job.status === 'confirmed';
}

  const handleChatNavigation = (selectedJob: any) => {
    console.log("selectedJob", selectedJob)
    console.log("client", selectedJob?.user?._id)
    console.log("worker", selectedJob?.assignedWorker?._id)
    navigate('/chat', {
      state: {
        clientId: selectedJob?.user?._id,
        providerId: selectedJob?.assignedWorker?._id,
        providerProfileId: selectedJob?.assignedWorker?.workerProfile,
        returnTo: '/my-proposals',
      },
    });
  }

  // Check if dispute can be created for a job
  const canCreateDispute = (job: any) => {
    if (!job) return false;
    // Check if user is the job owner (client) or assigned worker
    const isJobOwner = job.user?._id === user?._id;
    const isAssignedWorker = job.assignedWorker?._id === user?._id;
    if (!isJobOwner && !isAssignedWorker) return false;
    
    // Check if job status allows dispute creation
    // Allow disputes for jobs that have started (reached, in_progress, job_finished, completed)
    const allowedStatuses = ['reached','in_progress', 'job_finished', 'completed'];
    if (!allowedStatuses.includes(job.status)) return false;
    
    // Check if active dispute already exists
    const activeDispute = activeDisputes[job._id];
    if (activeDispute && (activeDispute.status === 'OPEN' || activeDispute.status === 'UNDER_REVIEW')) {
      return false;
    }
    
    return true;
  };

  // Fetch active disputes for jobs
  const fetchActiveDisputes = async () => {
    try {
      const res = await api.getDisputes({ status: 'OPEN,UNDER_REVIEW' });
      if (res && res.success && res.data?.data) {
        const disputesMap: Record<string, any> = {};
        res.data.data.forEach((dispute: any) => {
          if (dispute.job?._id) {
            disputesMap[dispute.job._id] = dispute;
          }
        });
        setActiveDisputes(disputesMap);
      }
    } catch (error) {
      console.error('Error fetching disputes:', error);
    }
  };

  // Dispute image handling
  const resizeDisputeImage = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("La imagen no puede superar los 10MB");
      return null;
    }

    return new Promise<Blob>((resolve, reject) => {
      Resizer.imageFileResizer(
        file,
        1500,
        1500,
        'JPEG',
        90,
        0,
        (uri: any) => {
          resolve(uri);
        },
        'blob'
      );
    });
  };

  const handleDisputeImageChange = (files: File[]) => {
    const totalImages = disputeImages.length + files.length;
    if (totalImages > 5) {
      toast.error(t('maxImages') || 'Solo puedes seleccionar hasta 5 imágenes.');
      return;
    }

    const newValidFiles: File[] = [];
    const newPreviewUrls: string[] = [];

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`La imagen "${file.name}" es mayor a 10MB y fue omitida.`);
        continue;
      }

      newValidFiles.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    }

    setDisputeImages(prev => [...prev, ...newValidFiles]);
    setDisputeImagePreviews(prev => [...prev, ...newPreviewUrls]);
  };

  const handleRemoveDisputeImage = (index: number) => {
    setDisputeImages(prev => prev.filter((_, i) => i !== index));
    setDisputeImagePreviews(prev => {
      const newPreviews = prev.filter((_, i) => i !== index);
      // Revoke object URL to free memory
      URL.revokeObjectURL(prev[index]);
      return newPreviews;
    });
  };

  const uploadDisputeImages = async (): Promise<string[]> => {
    if (disputeImages.length === 0) return [];

    const uploadedUrls: string[] = [];

    for (const image of disputeImages) {
      try {
        const resizedImage = await resizeDisputeImage(image);
        if (!resizedImage) continue;

        const formData = new FormData();
        formData.append('file', resizedImage);

        const response = await api.updateJobPhoto(formData);

        if (response && response.success) {
          uploadedUrls.push(response.data || response);
        } else {
          throw new Error(`Failed to upload image: ${response?.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Error al subir una imagen. Inténtalo de nuevo.');
      }
    }

    return uploadedUrls;
  };

  const handleSubmitDispute = async () => {
    if (!selectedProposal) return;

    // Validation
    if (!disputeIssueType) {
      toast.error(t('issueType') + ' es requerido');
      return;
    }

    if (!disputeDescription || disputeDescription.trim().length < 20) {
      toast.error(t('disputeDescription') + ' debe tener al menos 20 caracteres');
      return;
    }

    if (disputeDescription.length > 2000) {
      toast.error(t('disputeDescription') + ' no puede exceder 2000 caracteres');
      return;
    }

    setIsSubmittingDispute(true);
    try {
      // Upload images first
      const imageUrls = await uploadDisputeImages();

      // Create dispute
      const disputeData = {
        jobId: selectedProposal._id,
        issueType: disputeIssueType,
        description: disputeDescription.trim(),
        imageUrls: imageUrls,
        createdBy:  String(user?._id)
      };

      const res = await api.createDispute(disputeData);

      if (res && res.success) {
        toast.success(t('disputeCreated') || 'Disputa creada exitosamente');
        setShowDisputeForm(false);
        resetDisputeForm();
        fetchJobs();
        fetchActiveDisputes();
      } else {
        const errorMessage = res?.message || res?.data?.message || t('disputeError') || 'Error al crear la disputa';
        
        // Handle specific error messages
        if (errorMessage.includes('job status')) {
          toast.error(t('disputeNotAllowed') || 'Solo puedes crear una disputa para trabajos que han comenzado.');
        } else if (errorMessage.includes('active dispute')) {
          toast.error(t('activeDisputeExists') || 'Ya existe una disputa activa para este trabajo.');
        } else if (errorMessage.includes('authorized')) {
          toast.error(t('unauthorizedDispute') || 'Solo el dueño del trabajo o el trabajador asignado pueden crear una disputa.');
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error: any) {
      console.error('Error creating dispute:', error);
      const errorMessage = error?.response?.data?.message || error?.message || t('disputeError') || 'Error al crear la disputa';
      
      if (errorMessage.includes('job status')) {
        toast.error(t('disputeNotAllowed') || 'Solo puedes crear una disputa para trabajos que han comenzado.');
      } else if (errorMessage.includes('active dispute')) {
        toast.error(t('activeDisputeExists') || 'Ya existe una disputa activa para este trabajo.');
      } else if (errorMessage.includes('authorized')) {
        toast.error(t('unauthorizedDispute') || 'Solo el dueño del trabajo o el trabajador asignado pueden crear una disputa.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmittingDispute(false);
    }
  };

  const resetDisputeForm = () => {
    setDisputeIssueType('');
    setDisputeDescription('');
    setDisputeImages([]);
    // Revoke all object URLs
    disputeImagePreviews.forEach(url => URL.revokeObjectURL(url));
    setDisputeImagePreviews([]);
  };

  const openDisputeForm = (job?: any) => {
    const jobToUse = job || selectedProposal;
    if (!jobToUse) return;
    if (!canCreateDispute(jobToUse)) {
      const activeDispute = activeDisputes[jobToUse._id];
      if (activeDispute) {
        toast.error(t('activeDisputeExists') || 'Ya existe una disputa activa para este trabajo.');
      } else {
        toast.error(t('disputeNotAllowed') || 'Solo puedes crear una disputa para trabajos que han comenzado.');
      }
      return;
    }
    setSelectedProposal(jobToUse);
    resetDisputeForm();
    setShowDisputeForm(true);
  };

  const selectedProposalCommissions = getJobCommissions(selectedProposal);
  const paymentDetailsCommissions = getJobCommissions({
    budget: paymentDetails?.budget,
    commissions: paymentDetails?.commissions ?? selectedProposal?.commissions,
  });

  // Prefer server estimate fields after voucher preview; fall back to commissions helper.
  const estimateBudget = Number(paymentDetails?.budget ?? 0);
  const estimateTax =
    paymentDetails?.tax != null
      ? Number(paymentDetails.tax)
      : paymentDetailsCommissions.taxAmount;
  const estimateClientCharge =
    paymentDetails?.clientCharge != null
      ? Number(paymentDetails.clientCharge)
      : paymentDetailsCommissions.clientAmount;
  const estimateClientChargePct =
    paymentDetails?.clientChargePercentage != null
      ? Number(paymentDetails.clientChargePercentage)
      : paymentDetailsCommissions.clientPercentage;
  const estimateClientChargeBeforeDiscount =
    paymentDetails?.clientChargeBeforeDiscount != null
      ? Number(paymentDetails.clientChargeBeforeDiscount)
      : null;
  const clientDiscountApplied = Number(paymentDetails?.clientDiscountApplied ?? 0);
  const isVoucherCodeMismatch =
    !!appliedVoucherCode &&
    voucherCodeInput.trim().toUpperCase() !== appliedVoucherCode;

  // 
  return (
    <ContentContainer className='!px-2 m-0 bg-gray-100' >
      <div className='flex flex-col gap-3'>
        <h1 className='text-2xl text-black font-bold'>{t('myProposals')}</h1>

        <div className="flex flex-col gap-4">
          {(jobs?.data || []).map((job, i) => {
            const isCompleted = job.status === 'successfully_completed';
            const statusColor =
              job.status === 'pending'
                ? 'text-gray-500'
                : job.status === 'worker_on_way'
                  ? 'text-teal-500'
                  : job.status === 'in_progress' || job.status === 'confirmed'
                    ? 'text-blue-500'
                    : job.status === 'confirmed'
                      ? 'text-orange-500'
                        : job.status === 'reached'
                        ? 'text-orange-500'
                        : job.status === 'job_finished' || job.status === 'completed'
                          ? 'text-yellow-500'
                          : job.status === 'awaiting_payment'
                            ? 'text-blue-500'
                            : job.status === 'dispute'
                              ? 'text-red-500'
                              : job.status === 'accepted' || job.status === 'successfully_completed'
                                ? 'text-green-light'
                                : 'text-gray-500';




            const statusDot =
              job.status === 'pending'
                ? 'bg-gray-400'
                : job.status === 'worker_on_way'
                  ? 'bg-teal-500'
                  : job.status === 'reached'
                    ? 'bg-orange-500'
                    : job.status === 'in_progress' || job.status === 'confirmed'
                      ? 'bg-blue-500'
                        : job.status === 'awaiting_payment'
                        ? 'bg-blue-500'
                        : job.status === 'job_finished' || job.status === 'completed'
                          ? 'bg-yellow-500'
                          : job.status === 'confirmed'
                            ? 'bg-orange-500'
                            : job.status === 'dispute'
                              ? 'bg-red-500'
                              : job.status === 'accepted' || job.status === 'successfully_completed'
                                ? 'bg-green-500'
                                : 'bg-gray-400';



            const action = isCompleted ? 'Rating ★' : 'Awaiting confirmation';
            const actionColor = isCompleted
              ? 'bg-green-light text-white'
              : 'bg-darkBlue text-white';

            const labelColor = isCompleted
              ? 'text-white'
              : 'text-white';

            const paymentSummary = getPaymentSummary(job);
            const showAthPending =
              paymentSummary.clientPaymentStatus === 'pending' && paymentSummary.hasAthPayment;

            return (
              <div
                key={job._id}
                className="bg-white border border-gray-200 rounded-xl px-6 py-6 shadow-sm flex flex-col gap-2 cursor-pointer"
                onClick={() => {
                  setSelectedProposal(job);
                  setShowPopup(true);
                }}
              >
                <div className="grid xl:grid-cols-6 lg:grid-cols-5 md:grid-cols-3 grid-cols-2 gap-4 items-center">
                  <div className='flex flex-col gap-2'>
                    <span className='text-sm font-semibold text-black '>{t('service_provider')}</span>
                    <span className="truncate max-w-[120px]" title={`${job.assignedWorker?.first_name || 'N/A'} ${job.assignedWorker?.last_name || ''}`}>{`${job.assignedWorker?.first_name || 'N/A'} ${job.assignedWorker?.last_name || ''}`}</span>
                  </div>
                  <div className='flex flex-col gap-2'>
                    <span className='text-sm font-semibold text-black'>{t('service_name')}</span>
                    <span className="truncate max-w-[140px]" title={getServiceName(job)}>{getServiceName(job)}</span>
                  </div>

                  <div className='flex flex-col gap-2'>
                    <span className='text-sm font-semibold text-black'>{t('proposedDate')}</span>
                    <span>{moment(job.createdAt).format('DD MMM YYYY')}</span>
                  </div>

                  <div className='flex flex-col gap-2'>
                    <span className='text-sm font-semibold text-black'>{t('chatColumn')}</span>
                    <span>
                      <IconButton size='small' onClick={(e) => {
                        e.stopPropagation();
                        handleChatNavigation(job);
                      }}>
                        <ChatBlackIcon />
                      </IconButton>
                    </span>
                  </div>

                  <div className='flex flex-col gap-2'>
                    <span className='text-sm font-semibold text-black'>{t('status')}</span>
                    <span className={`flex items-center gap-1 font-semibold ${statusColor}`}> <span className={`w-2 h-2 min-w-[8px] min-h-[8px] rounded-full ${statusDot}`}></span> {t(JobStatusDetailStatus[job.status])} </span>
                    {showAthPending && (
                      <span className="text-xs font-medium text-amber-600">{t('paymentStatus_pending')}</span>
                    )}
                  </div>

                  <div className='flex flex-col gap-2'>
                    <span className='text-sm font-semibold text-black'></span>
                    <div className="flex flex-col gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}>
                      {isCompleted ? (
  // --- ✅ REVIEW BUTTON ---
  <CustomButton
    icon={<StarIcon />}
    label={job?.reviewed ? t('reviewed') : t('review')}
    type='button'
    className="px-6 py-2 !rounded-3xl bg-blue-50 text-blue-500 border border-blue-200 font-semibold flex items-center gap-1 cursor-pointer"
    handleButtonClick={e => {
      console.log("job", job);
      setSelectedProposal(job);
      setShowRatingPopup(true);
    }}
    disabled={job?.reviewed}
  />
) : job.status === 'pending' ? (
  // --- ⚙️ PENDING PROPOSAL BUTTONS ---
  <div className="flex items-center gap-2 flex-wrap w-full">
    <CustomButton
      label={t('accept&pay')}
      className="px-3 py-2 !rounded-3xl bg-green-400 border font-semibold cursor-pointer text-sm"
      // labelStyle="whitespace-nowrap"
      handleButtonClick={() =>{
        setSelectedProposal(job)
        setPendingAction({
          jobId: job._id,
          action: 'accept',
          label: 'Accept & Pay',
          budget: job.budget
         // message: 'Are you sure you want to accept this proposal?',
        })
        setShowVerificationPopup(true)
      }
      }
    />
    <CustomButton
      label={t('reject')}
      className="px-3 py-2 !rounded-3xl bg-red-400 border font-semibold cursor-pointer text-sm"
      handleButtonClick={() =>{
        setPendingAction({
          jobId: job._id,
          action: 'reject',
          label: 'Reject Proposal',
          //message: 'Are you sure you want to reject this proposal?',
        })
        setShowVerificationPopup(true)
      }
      }
    />
  </div>
) :   job.status === 'cancelled' || job.status === 'rejected' || job.status === 'dispute' ? (
  null ) : job.status === 'worker_on_way' ? (
  <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
    <span className="text-sm text-teal-600 font-medium">
      {t('waitingForProviderArrival')}
    </span>
    <CustomButton
      label={t('markAsArrived')}
      type="button"
      className="px-6 py-2 !rounded-3xl bg-darkBlue text-white font-semibold cursor-pointer whitespace-nowrap"
      labelStyle="text-white"
      handleButtonClick={() => {
        setPendingAction({
          jobId: job._id,
          action: 'worker_on_way',
          label: t('markAsArrived'),
        });
        setShowVerificationPopup(true);
      }}
      disabled={isLoading}
    />
  </div>
) : (
  // --- ⚙️ NORMAL ACTION BUTTONS ---
  <div className="flex items-center gap-2 flex-wrap">
    <CustomButton
      label={
        job.status === 'awaiting_payment' && showAthPending
          ? t('paymentStatus_pending')
          : job.status === 'awaiting_payment' ||
            job.status === 'reached' ||
            job.status === 'successfully_completed' ||
            job.status === 'in_progress' ||
            job.status === 'job_finished' ||
            job.status === 'completed'
            ? t(JobStatusButtonText_User[job.status])
            : t('startJob')
      }
      labelStyle={labelColor}
      className={`px-6 py-2 whitespace-nowrap !rounded-3xl font-semibold ${actionColor} cursor-pointer ${
        (job.status !== 'awaiting_payment' &&
          job.status !== 'worker_on_way' &&
          job.status !== 'reached' &&
          job.status !== 'in_progress' &&
          job.status !== 'job_finished' &&
          job.status !== 'completed') 
          ? 'disabled:opacity-50' 
          : ''
      }`}
      handleButtonClick={
        job.status === 'awaiting_payment'
          ? () => {
              setSelectedProposal(job);
              openPaymentDetailsForJob(job._id);
            }
            : job.status === 'reached'
              ? () => {
                  setPostServiceJob(job);
                  setShowPostServiceModal(true);
                }
              : job.status === 'in_progress'
                ? () => {
                    setPostServiceJob(job);
                    setShowPostServiceModal(true);
                  }
                : job.status === 'job_finished' || job.status === 'completed'
                  ? () => {
                      setPostServiceJob(job);
                      setShowPostServiceModal(true);
                    }
                  : undefined
      }
      disabled={
        (job.status !== 'awaiting_payment' &&
          job.status !== 'worker_on_way' &&
          job.status !== 'reached' &&
          job.status !== 'in_progress' &&
          job.status !== 'job_finished' &&
          job.status !== 'completed') ||
        (job.status === 'awaiting_payment' && (stripeLoading || showAthPending)) ||
        (job.status === 'awaiting_payment' && paymentSummary.clientPaymentStatus === 'confirmed')
      }
      isLoading={job.status === 'awaiting_payment' && stripeLoading}
    />
    
    {/* Create Dispute Button */}
    {canCreateDispute(job) && (
      <CustomButton
        label={t('createDispute')}
        type='button'
        className="px-6 py-2 !rounded-3xl bg-red-500 text-white border font-semibold cursor-pointer"
        labelStyle="text-white"
        handleButtonClick={(e) => {
          if (e) e.stopPropagation();
          openDisputeForm(job);
        }}
        disabled={isSubmittingDispute}
      />
    )}

    {isRefundable(job) && (
      <CustomButton
        label={t('refund')}
        type='button'
        className="px-6 py-2 !rounded-3xl bg-blue-50 text-blue-500 border border-blue-200 font-semibold flex items-center gap-1 cursor-pointer"
        handleButtonClick={e => { setPendingAction({ jobId: job._id, action: 'refund_payment', label: 'Reembolsar pago' }); setShowVerificationPopup(true); }}
        disabled={isLoading}
      />
    )}
  </div>
)}

                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {!fetchingJobs && (!jobs?.data || jobs.data.length === 0) && (
            <div className="flex justify-center items-center h-full">
              <span className="text-gray-500 text-sm">{t('noProposalsYet')}</span>
            </div>
          )}
        </div>
        {fetchingJobs && (
         <div className="flex flex-col gap-4">
              {[...Array(5)].map((_, i) => (
                <JobSkeleton key={i} />
              ))}
            </div>
        )}
        <div className="flex justify-center">


          <Pagination
            count={jobs?.totalPages || 1}
            color="primary"
            variant="outlined"
            page={offset}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
            siblingCount={0}
          />
        </div>
      </div>

      {/* Popup for proposal details */}
   <Popup
  isOpen={showPopup}
  handleClose={() => setShowPopup(false)}
  className="bg-white"
  size="md"
  headerTitle={t('view_proposals')}
  isShowCloseIcon
  isShowBlackCrossIcon
  isShowDivider
  bodyChildren={
    selectedProposal && (
      <div className="p-4 w-full overflow-y-auto max-h-[80vh]"> {/* vertical scroll only */}
        {/* Prevent horizontal scroll */}
        <div className="w-full overflow-x-hidden break-words">

          {/* Details grid */}
          <div className="grid lg:grid-cols-5 md:grid-cols-4 grid-cols-2 w-full gap-2 gap-y-4 text-sm font-semibold border-b border-gray-200 pb-2 mb-4">
            <div>
              <div className="text-sm text-black font-semibold mb-1">{t('jobId')}</div>
              <div className="font-normal text-black whitespace-normal break-words">
                {selectedProposal?.uid || 'N/A'}  
              </div>
            </div>
            <div>
              <div className="text-sm text-black font-semibold mb-1">{t('service_provider')}</div>
              <div className="font-normal text-black whitespace-normal break-words">
                {selectedProposal.assignedWorker?.first_name || 'N/A'}  {selectedProposal.assignedWorker?.last_name || 'N/A'}
              </div>
            </div>
            <div className="col-span-full">
              <JobPaymentSummarySection
                summary={getPaymentSummary(selectedProposal)}
                variant="client"
              />
            </div>
            <JobCommissionBreakdown job={selectedProposal} variant="client" />
            <div>
              <div className="text-sm text-black font-semibold mb-1">{t('service_name')}</div>
              <div className="font-normal text-black whitespace-normal break-words">
                {getServiceName(selectedProposal)}
              </div>
            </div>
            <div>
              <div className="text-sm text-black font-semibold mb-1">{t('date')}</div>
              <div className="font-normal text-black">
                {moment(selectedProposal.createdAt).format('DD MMM YYYY')}
              </div>
            </div>
            <div>
              <div className="text-sm text-black font-semibold mb-1">{t('status')}</div>
              <div
                className={`font-semibold ${
                  selectedProposal.status === 'pending'
                    ? 'text-yellow-500'
                    : selectedProposal.status === 'accepted' || selectedProposal.status === 'in_progress'
                    ? 'text-green-500'
                    : 'text-gray-500'
                }`}
              >
                {t(JobStatusDetailStatus[selectedProposal.status])}
              </div>
            </div>
          </div>

          <Divider className="border-gray-400 my-4" />

          {/* Description */}
          <div className="mb-4">
            <div className="font-semibold mb-1">{t('description')}:</div>
            <div className="text-gray-700 text-sm mb-5 whitespace-pre-wrap break-words">
              {selectedProposal.description}
            </div>
          </div>

          <Divider className="border-gray-400 my-4" />

          {/* Attachments */}
          <div className="mb-4">
            <div className="font-semibold mb-1">{t('attachment')}:</div>
            <div className="flex flex-wrap gap-3">
              {selectedProposal.imageUrls && selectedProposal.imageUrls.length > 0 ? (
                selectedProposal.imageUrls.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`attachment-${idx}`}
                    className="h-20 w-20 object-cover rounded-md border cursor-pointer hover:scale-105 transition-transform duration-200"
                    onClick={() => setPreviewImage(url)}
                  />
                ))
              ) : (
                <span className="text-gray-400 text-xs">{t('noAttachments')}</span>
              )}
            </div>
          </div>

          {/* Check-In Location */}
          {selectedProposal.assignedWorker?._id && selectedProposal.status === 'worker_on_way' && (
            <div className="mb-2">
             
              <div className="font-semibold mb-1">{t('liveLocation')}:</div>
              <LiveLocation
                userId={selectedProposal.assignedWorker._id}
                jobLocation={selectedProposal.location}
                jobId={selectedProposal._id}
              />
            </div>
          )}

          {/* Active Dispute Badge */}
          {activeDisputes[selectedProposal._id] && (
            <>
              <Divider className="border-gray-400 my-4" />
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="font-semibold text-yellow-800 mb-1">{t('dispute')}</div>
                <div className="text-sm text-yellow-700">
                  {t('activeDisputeExists')} {t('disputeStatus')}: {t(activeDisputes[selectedProposal._id].status.toLowerCase().replace('_', ''))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }
/>

      {/* Rating Popup */}
      <Popup
        isOpen={showRatingPopup}
        handleClose={() => setShowRatingPopup(false)}
        className="bg-white"
        size="md"
        headerTitle={t('rateExperience')}
        isShowCloseIcon
        isShowBlackCrossIcon
        bodyChildren={(
          <div className="p-2 pl-0 w-full">
            <Rating
              name="rating"
              value={rating}
              precision={0.5}
              max={5}
              onChange={(event, newValue) => {
                console.log("newValue", newValue)
                setRating(newValue);
              }}
              sx={{ color: '#4285F4 !important', fontSize: 32 }}
            />

            <div className="font-bold mb-2 mt-6">{t('leaveFeedback')}</div>
            <TextArea
              name="feedback"
              value={feedback}
              labelClassName='!text-black text-sm'
              onChange={e => setFeedback(e.target.value)}
              placeholder={t('write')}
              maxLength={MAX_FEEDBACK_LENGTH}
              className="w-full min-h-[120px] rounded text-base"
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-gray-400 ml-auto">{t('characters')} {feedback.length}/{MAX_FEEDBACK_LENGTH}</span>
            </div>
            <div className="flex gap-4 justify-end mt-6">
              <CustomButton
                label={t('submitReview')}
                className="px-8 py-3 rounded-3xl bg-darkBlue"
                labelStyle='text-md text-white'
                handleButtonClick={handleSubmitReview}
              />
              <CustomButton
                label={t('cancel')}
                className="!rounded-3xl px-8 py-3 border bg-opacity-10 border-darkBlue border-opacity-40  bg-blue-100 " labelStyle="text-sm text-darkBlue"
                handleButtonClick={() => setShowRatingPopup(false)}
              />
            </div>
          </div>
        )}
      />

                {previewImage && (
            <Popup
              isOpen={!!previewImage}
              handleClose={() => setPreviewImage(null)}
              size="lg"
              className="bg-black bg-opacity-80 flex items-center justify-center"
              isShowCloseIcon
              isShowBlackCrossIcon
              bodyChildren={
                <div className="flex justify-center items-center w-full h-full">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="max-h-[90vh] max-w-full object-contain rounded-lg shadow-lg"
                  />
                </div>
              }
            />
          )}

      <Popup
        isOpen={showStripePopup}
        handleClose={() => setShowStripePopup(false)}
        className="bg-white"
        size="xs"
        headerTitle={t('payment_info')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        contentSx={{ padding: 0 }}
        bodyChildren={stripeClientSecret && (
          <div className='h-full w-full'>
            <StripeCheckout
              clientSecret={stripeClientSecret}
              onPaymentMethodSaved={async (paymentMethodId) => {
                console.log('Payment Method ID:', paymentMethodId);
                setSavedPaymentMethodId(paymentMethodId);
                addStripePaymentMethod(paymentMethodId)
              }}
            />
          </div>
        )}
      />
      {/* Payment Details Popup */}
      <Popup
        slotProps={{ preventOutsideClose: true }}
        isOpen={showPaymentDetailsPopup}
        handleClose={() => {
          setShowPaymentDetailsPopup(false);
          setVoucherCodeInput('');
          setAppliedVoucherCode(null);
        }}
        className="bg-white"
        size="md"
        headerTitle={t('payment_summary')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={selectedProposal && paymentDetails && (
          <div className="p-6 w-full">
            {/* Job Details Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('job_details')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">{t('service_provider')}</div>
                  <div className="font-semibold text-gray-800">{`${selectedProposal.assignedWorker?.first_name || 'N/A'} ${selectedProposal.assignedWorker?.last_name || ''}`}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">{t('service_name')}</div>
                  <div className="font-semibold text-gray-800">{getServiceName(selectedProposal)}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">{t('date')}</div>
                  <div className="font-semibold text-gray-800">{moment(selectedProposal.createdAt).format('DD MMM YYYY')}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">{t('status')}</div>
                  <div className={`font-semibold ${selectedProposal.status === 'pending' ? 'text-yellow-500' : selectedProposal.status === 'accepted' || selectedProposal.status === 'in_progress' ? 'text-green-500' : 'text-gray-500'}`}>
                    {t(JobStatusDetailStatus[selectedProposal?.status])}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <JobPaymentSummarySection
                  summary={getPaymentSummary(selectedProposal)}
                  variant="client"
                  className="text-sm"
                />
              </div>
            </div>

            {/* Payment Details Section */}
            <div>
  <h3 className="text-lg font-semibold text-gray-800 mb-4">
    {t('payment_breakdown')}
  </h3>

  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
    {/* Voucher: preview only; redemption happens on pay */}
    <div className="mb-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="text"
          value={voucherCodeInput}
          onChange={(e) => {
            const next = e.target.value;
            setVoucherCodeInput(next);
            // If the user clears the input after applying, refresh estimate without voucher.
            if (next.trim() === '' && appliedVoucherCode && !isApplyingVoucher) {
              handleClearVoucher();
            }
          }}
          placeholder={t('voucher_code')}
          className="w-full sm:w-64 border border-blue-200 rounded-lg px-3 py-2 text-black focus:outline-none focus:ring-2 focus:ring-darkBlue focus:border-darkBlue"
          aria-label={t('voucher_code')}
        />

        <div className="flex items-center gap-3">
          <CustomButton
            type="button"
            label={t('apply_voucher')}
            className="!rounded-3xl !bg-darkBlue px-5 py-2"
            labelStyle="text-sm text-white"
            isLoading={isApplyingVoucher}
            disabled={!voucherCodeInput.trim() || isApplyingVoucher}
            handleButtonClick={handleApplyVoucher}
          />

          {appliedVoucherCode && (
            <CustomButton
              type="button"
              label={t('clear_voucher_code')}
              className="!rounded-3xl border border-darkBlue bg-white px-5 py-2"
              labelStyle="text-sm text-darkBlue"
              isLoading={isApplyingVoucher}
              disabled={isApplyingVoucher}
              handleButtonClick={handleClearVoucher}
            />
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-2">{t('voucher_apply_help')}</p>

      <AssignedVoucherPicker
        vouchers={usableAssignedVouchers}
        selectedCode={appliedVoucherCode || voucherCodeInput}
        disabled={isApplyingVoucher}
        onSelect={(code) => {
          setVoucherCodeInput(code);
          handleApplyVoucher(code);
        }}
      />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

      {/* Left Column: Breakdown — trust estimate fields from GET /job/payment */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">{t('budget')}</span>
          <span className="font-semibold text-gray-800">
            ${estimateBudget.toFixed(2)}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600">{t('governmentTax')} ({paymentDetailsCommissions.taxPercentage}%)</span>
          <span className="font-semibold text-gray-800">
            ${estimateTax.toFixed(2)}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-600">
            {t('experienceUpgrade')} ({estimateClientChargePct}%)
          </span>
          <span className="font-semibold text-gray-800">
            ${estimateClientCharge.toFixed(2)}
          </span>
        </div>

        {clientDiscountApplied > 0 && estimateClientChargeBeforeDiscount != null && (
          <div className="text-xs text-gray-500">
            {t('voucher_fee_was_now', {
              before: estimateClientChargeBeforeDiscount.toFixed(2),
              after: estimateClientCharge.toFixed(2),
              pct: estimateClientChargePct,
            })}
          </div>
        )}

        {clientDiscountApplied > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600">{t('voucher_discount')}</span>
            <span className="font-semibold text-green-700">-${clientDiscountApplied.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Right Column: Total */}
      <div className="border-t md:border-t-0 md:border-l border-blue-200 pt-4 md:pt-0 md:pl-4">
        <div className="flex justify-between items-center text-lg font-bold text-blue-600">
          <span>{t('total_charge')}</span>
          <span>${roundMoney(paymentDetails.totalCharge).toFixed(2)}</span>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          *{t('transparent_fees_note')}
        </div>
      </div>
    </div>
  </div>
</div>


            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <CustomButton
                label={t('close')}
                className="!rounded-3xl border bg-opacity-10 border-darkBlue border-opacity-40  bg-blue-100 px-6 py-2"
                labelStyle="text-sm text-darkBlue"
                handleButtonClick={() => {
                  setShowPaymentDetailsPopup(false);
                  setPaymentFlowStep('initial');
                  setVoucherCodeInput('');
                  setAppliedVoucherCode(null);
                }}
              />
              <CustomButton
                isLoading={isPaymentInitiated}
                label={t('confirm_payment')}
                className="!rounded-3xl !bg-darkBlue px-6 py-2" labelStyle="text-sm text-white"
                handleButtonClick={() => {
                  if (paymentFlowStep === 'initial') {
                    setShowPaymentDetailsPopup(false);
                    setShowPaymentMethodChoice(true);
                  } else if (paymentFlowStep === 'stripe_selected') {
                    initiateStripeJobPayment(selectedProposal._id);
                  } else if (paymentFlowStep === 'ath_selected') {
                    initiateAthJobPayment(selectedProposal._id, athPhoneNumber);
                  }
                }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-3" style={{ lineHeight: '1.4' }}>
              <span className="font-medium">{t('paymentAuthTitle')}</span>
              <br />
              {t('paymentAuthDescription')}
            </p>
          </div>
        )}
      />
      {/* Stripe or ATH choice popup */}
      <Popup
        slotProps={{ preventOutsideClose: true }}
        isOpen={showPaymentMethodChoice}
        handleClose={() => setShowPaymentMethodChoice(false)}
        className="bg-white"
        size="xs"
        headerTitle={t('receivePaymentsVia')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={
          <div className="p-6 flex flex-col gap-4">
            <p className="text-gray-700 text-sm">{t('choosePaymentMethod')}</p>
            <div className="flex flex-col gap-3">
              <CustomButton
                label={t('paymentTypeStripe')}
                className="!rounded-3xl !bg-darkBlue px-6 py-3 w-full"
                labelStyle="text-white font-medium"
                handleButtonClick={() => {
                  setShowPaymentMethodChoice(false);
                  setPaymentFlowStep('stripe_selected');
                  handleStripePay();
                }}
              />
              <CustomButton
                label={t('paymentTypeAth')}
                className="!rounded-3xl border border-darkBlue bg-white px-6 py-3 w-full"
                labelStyle="text-darkBlue font-medium"
                handleButtonClick={() => {
                  setShowPaymentMethodChoice(false);
                  setPaymentFlowStep('ath_selected');
                  setShowAthPhonePopup(true);
                }}
              />
            </div>
          </div>
        }
      />
      {/* ATH phone number popup — confirm then shows payment summary */}
      <Popup
        slotProps={{ preventOutsideClose: true }}
        isOpen={showAthPhonePopup}
        handleClose={() => {
          setShowAthPhonePopup(false);
          setAthPhoneNumber('');
        }}
        className="bg-white"
        size="xs"
        headerTitle={t('athPaymentRequest')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={
          <div className="p-6 flex flex-col gap-4">
            <p className="text-gray-700 text-sm">{t('athEnterPhoneNumber')}</p>
            <input
              type="tel"
              value={athPhoneNumber}
              onChange={(e) => setAthPhoneNumber(e.target.value)}
              placeholder={t('athPhonePlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-darkBlue focus:border-darkBlue"
              aria-label={t('phone')}
            />
            <div className="flex justify-end gap-2">
              <CustomButton
                label={t('cancel')}
                className="!rounded-3xl border border-gray-400 px-6 py-2"
                labelStyle="text-gray-700"
                handleButtonClick={() => {
                  setShowAthPhonePopup(false);
                  setAthPhoneNumber('');
                }}
              />
              <CustomButton
                label={t('confirm_payment')}
                className="!rounded-3xl !bg-darkBlue px-6 py-2"
                labelStyle="text-white"
                isLoading={isValidatingAthPhone}
                handleButtonClick={async () => {
                  const trimmed = athPhoneNumber.replace(/\D/g, '');
                  if (!trimmed || trimmed.length < 10) {
                    toast.error(t('athPhoneInvalid') || 'Please enter a valid phone number');
                    return;
                  }
                  if (!selectedProposal?._id) return;

                  setIsValidatingAthPhone(true);
                  try {
                    const validateRes = await api.validateAthMovilUser(trimmed);
                    if (!validateRes?.success) {
                      toast.error(
                        validateRes?.message ||
                          t('athPhoneNotAthAccount') ||
                          t('athPhoneInvalid')
                      );
                      return;
                    }

                    const [jobPaymentDetails] = await Promise.all([
                      appliedVoucherCode
                        ? api.getJobPaymentDetails(selectedProposal._id, appliedVoucherCode)
                        : api.getJobPaymentDetails(selectedProposal._id),
                      loadUsableClientVouchers(),
                    ]);
                    const estimate = unwrapPaymentEstimate(jobPaymentDetails);
                    if (estimate) {
                      setPaymentDetails(estimate);
                      setShowAthPhonePopup(false);
                      setShowPaymentDetailsPopup(true);
                    } else {
                      toast.error(jobPaymentDetails?.message || 'No se pudieron cargar los detalles del pago');
                    }
                  } catch {
                    toast.error('No se pudieron cargar los detalles del pago');
                  } finally {
                    setIsValidatingAthPhone(false);
                  }
                }}
              />
            </div>
          </div>
        }
      />
      <VerifySubmission
        isOpen={showVerificationPopup}
        handleClose={cancelStatusChange}
        handleSubmit={confirmStatusChange}
        isLoading={isLoading}
        size="xs"
        headerTitle={pendingAction?.action === 'accept' ? t('confirmYourService') : undefined}
        cancelButtonLabel={pendingAction?.action === 'accept' ? t('cancel') : undefined}
        confirmButtonLabel={pendingAction?.action === 'accept' ? t('confirmService') : undefined}
      >
       {pendingAction && (
  <div style={{ fontFamily: 'Object Sans, sans-serif' }}>
    {/* Only show new UI for accept action with budget */}
    {(pendingAction.action === 'accept' && pendingAction?.budget) ? (
      <>
        {/* Description */}
        <p className="text-center mb-6" style={{ fontSize: '15px', color: '#333333', lineHeight: '1.5' }}>
          {t('clientConfirmDescription')}
        </p>

        {/* Breakdown Card */}
        <div className="w-full mb-6" style={{ backgroundColor: '#F5F5F3', border: '1px solid #E5E5E5', borderRadius: '8px', padding: '16px' }}>
          <div className="flex flex-col" style={{ fontSize: '15px', color: '#333333', gap: '8px' }}>
            <div className="flex justify-between">
              <span>{t('serviceAmount')}:</span>
              <span className="font-medium">${pendingAction.budget.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t('experienceUpgrade')} ({selectedProposalCommissions.clientPercentage}%):</span>
              <span className="font-medium">${selectedProposalCommissions.clientAmount.toFixed(2)}</span>
            </div>

             <div className="flex justify-between">
              <span>{t('governmentTax')} ({selectedProposalCommissions.taxPercentage}%):</span>
              <span className="font-medium">${selectedProposalCommissions.taxAmount.toFixed(2)}</span>
            </div>

            <div className="flex justify-between pt-2 mt-2" style={{ borderTop: '1px solid #E5E5E5' }}>
              <span className="font-bold" style={{ fontSize: '17px', color: '#000000' }}>{t('totalToPay')}:</span>
              <span className="font-bold" style={{ fontSize: '17px', color: '#4285F4' }}>${roundMoney(selectedProposalCommissions.totalClientCharge).toFixed(2)}</span>
            </div>
            <p className="text-sm mt-2" style={{ color: '#64748b', lineHeight: '1.4' }}>
              {t('paymentAuthOnCompletion')}
            </p>
            <p className="text-sm mt-3" style={{ color: '#166534', lineHeight: '1.4' }}>
              {t('clientConfirmVoucherNote')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center mb-4" style={{ fontSize: '15px', color: '#333333', lineHeight: '1.5' }}>
          {t('clientConfirmFooter')}
        </p>
      </>
    ) : (
      <>
        {/* Action confirmation title */}
        <div className="text-lg font-semibold text-gray-800 mb-2 text-center">
          {t('confirmAction', { action: pendingAction.label.toLowerCase() })}
        </div>

        {/* Warning about irreversible action */}
        <div className="text-sm text-gray-600 mb-4 text-center">
          {t('irreversible_action')}
        </div>
      </>
    )}
  </div>
)}


      </VerifySubmission>

      {/* Post-service modal (client): when provider marked job as finished */}
      <Popup
        isOpen={showPostServiceModal}
        handleClose={() => {
          setShowPostServiceModal(false);
          setPostServiceJob(null);
        }}
        className="bg-white"
        size="xs"
        headerTitle={t('postServiceModalTitle')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={
          <div className="p-4 w-full" style={{ fontFamily: 'Object Sans, sans-serif' }}>
            <p className="text-center mb-6" style={{ fontSize: '15px', color: '#333333', lineHeight: '1.5' }}>
              {t('postServiceModalDescription')}
            </p>
          </div>
        }
        footerChildren={
          <div className="w-full flex flex-col gap-3 px-4 pb-4">
            <CustomButton
              label={t('postServiceModalMarkCompleted')}
              className="!rounded-3xl !bg-darkBlue px-6 py-2 w-full"
              labelStyle="text-sm text-white"
              handleButtonClick={() => {
                if (!postServiceJob) return;
                setShowPostServiceModal(false);
                setPendingAction({
                  jobId: postServiceJob._id,
                  action: 'release_payment',
                  label: 'Release Payment',
                });
                setShowVerificationPopup(true);
                setPostServiceJob(null);
              }}
            />
            <button
              type="button"
              onClick={() => {
                if (!postServiceJob) return;
                setShowPostServiceModal(false);
                openDisputeForm(postServiceJob);
                setPostServiceJob(null);
              }}
              className="text-sm text-darkBlue font-medium underline hover:no-underline bg-transparent border-none cursor-pointer"
            >
              {t('postServiceModalReportIssue')}
            </button>
          </div>
        }
      />

      {/* Dispute Form Popup */}
      <Popup
        isOpen={showDisputeForm}
        handleClose={() => {
          setShowDisputeForm(false);
          resetDisputeForm();
        }}
        className="bg-white"
        size="md"
        headerTitle={t('createDispute')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={
          <div className="p-4 w-full">
            <div className="flex flex-col gap-4">
              {/* Issue Type */}
              <FormControl fullWidth required>
                <InputLabel>{t('issueType')}</InputLabel>
                <Select
                  value={disputeIssueType}
                  onChange={(e) => setDisputeIssueType(e.target.value)}
                  label={t('issueType')}
                >
                  <MenuItem value="payment_issue">{t('paymentIssue')}</MenuItem>
                  <MenuItem value="work_quality">{t('workQuality')}</MenuItem>
                  <MenuItem value="behavior">{t('behavior')}</MenuItem>
                  <MenuItem value="other">{t('other')}</MenuItem>
                </Select>
              </FormControl>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-black">
                  {t('disputeDescription')} <span className="text-red-500">*</span>
                </label>
                <TextArea
                  name="disputeDescription"
                  value={disputeDescription}
                  onChange={(e) => setDisputeDescription(e.target.value)}
                  placeholder={t('disputeDescriptionPlaceholder')}
                  className="w-full min-h-[120px] rounded text-base"
                  maxLength={2000}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-400">
                    {disputeDescription.length}/2000 {t('characters')}
                  </span>
                  {disputeDescription.length > 0 && disputeDescription.length < 20 && (
                    <span className="text-xs text-red-500">
                      Mínimo 20 caracteres
                    </span>
                  )}
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-black">
                  {t('uploadImages')} ({t('maxImages')})
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      handleDisputeImageChange(Array.from(e.target.files));
                    }
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="dispute-image-upload"
                />
                <label
                  htmlFor="dispute-image-upload"
                  className="block w-full p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors text-center"
                >
                  <span className="text-gray-600">{t('uploadImages')}</span>
                </label>

                {/* Image Previews */}
                {disputeImagePreviews.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-4">
                    {disputeImagePreviews.map((preview, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={preview}
                          alt={`Preview ${idx + 1}`}
                          className="h-20 w-20 object-cover rounded-md border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveDisputeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-end mt-4">
                <CustomButton
                  label={t('cancel')}
                  className="!rounded-lg px-6 py-3 border bg-white border-gray-300"
                  labelStyle="text-base text-gray-700"
                  handleButtonClick={() => {
                    setShowDisputeForm(false);
                    resetDisputeForm();
                  }}
                />
                <CustomButton
                  label={t('submitDispute')}
                  className="!rounded-lg px-6 py-3 bg-red-500"
                  labelStyle="text-base text-white"
                  handleButtonClick={handleSubmitDispute}
                  disabled={isSubmittingDispute || !disputeIssueType || disputeDescription.trim().length < 20}
                  isLoading={isSubmittingDispute}
                />
              </div>
            </div>
          </div>
        }
      />
    </ContentContainer>
  );
};

export default MyProposals;
